<template>
  <ItemListCollectionWrapper
    class="collectionWrapper"
    collectionModule="CloudInsightsEventsCollection"
    :defaultFilters="getFilters"
    defaultSortBy="eventTime"
    :refreshKey="refreshKey"
    :rowsPerPageLocalStorageKey="rowsPerPageLocalStorageKey"
  >
    <template v-slot="slotProps">
      <ItemListPagination
        :columns="selectedColumns"
        :currentPageNum="slotProps.currentPageNum"
        :isLoading="isComponentLoading(slotProps)"
        :itemComponent="itemComponent"
        :rowsPerPage="slotProps.rowsPerPage"
        :showEmptyState="shouldShowEmptyState(slotProps)"
        :sortBy="slotProps.sortBy"
        :sortDirection="slotProps.sortDirection"
        :tableData="slotProps.tableData"
        :totalCount="slotProps.totalCount"
        @pageChange="slotProps.handlePageChange"
        @sortColumn="slotProps.handleSortColumn"
      >
        <template v-slot:toolbar>
          <div>
            <CloudInsightsItemListToolbarV2
              v-if="$hasFeatureFlag($featureFlags.cloudInsightsToolbarV2)"
              class="toolbar"
              :rowsPerPage="slotProps.rowsPerPage"
              :selectedColumns="selectedColumns"
              :totalEvents="slotProps.totalCount"
              @columnsChange="handleColumnsChange"
            />
            <CloudInsightsItemListToolbar
              v-else
              class="toolbar"
              :rowsPerPage="slotProps.rowsPerPage"
              :selectedColumns="selectedColumns"
              :totalEvents="slotProps.totalCount"
              @columnsChange="handleColumnsChange"
              @search="slotProps.handleEnterSearchTerm"
            />
            <UpgradeNudge
              class="upgradeNudge"
              :data-test-id="$testId('upgradeNudge')"
            />
          </div>
        </template>
        <template
          v-if="!slotProps.tableData.length && !isLoading"
          v-slot:emptyState
        >
          <NoItems
            :src="noResultsIllustration"
            title="No events exist"
          />
        </template>
      </ItemListPagination>
    </template>
  </ItemListCollectionWrapper>
</template>

<script>
import { ItemListPagination, Types } from '@jumpcloud/ui-components';
import { columnsData, defaultColumnsData } from 'client/CloudInsights/constants/CloudInsightsConstants';
import { mapGetters, mapState } from 'vuex';
import Constants, {
  ItemTypeConsts,
  LocalStorageConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import ItemListCollectionWrapper from 'client/components/ItemList/ItemListCollectionWrapper.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import NoItems from 'client/components/ItemList/NoItems.vue';

import CloudInsightsItemListToolbar from 'client/CloudInsights/CloudInsightsItemList/CloudInsightsItemListToolbar.vue';
import CloudInsightsItemListToolbarV2 from 'client/CloudInsights/CloudInsightsItemList/CloudInsightsItemListToolbarV2.vue';
import CloudInsightsItemRow from 'client/CloudInsights/CloudInsightsItemList/CloudInsightsItemRow.vue';
import UpgradeNudge from 'client/components/Nudge/UpgradeNudge.vue';

const { noResultsIllustration } = Constants;
const { cloudInsightsEventsRowsPerPage, cloudInsightsEventsSelectedColumns } = LocalStorageConsts;
const cloudInsightsEventsModule = StoreModuleMap[ItemTypeConsts.cloudInsightsEvents];
const { Column } = Types;

export default {
  name: 'CloudInsightsItemList',

  components: {
    ItemListCollectionWrapper,
    ItemListPagination,
    CloudInsightsItemListToolbar,
    CloudInsightsItemListToolbarV2,
    NoItems,
    UpgradeNudge,
  },

  LocalStorageService,

  props: {
    mainLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedColumns: [],
      refreshKey: 0,
    };
  },

  computed: {
    ...mapGetters(cloudInsightsEventsModule, ['getFilters']),
    ...mapState(cloudInsightsEventsModule, ['isLoading']),
  },

  created() {
    this.itemComponent = CloudInsightsItemRow;
    this.noResultsIllustration = noResultsIllustration;
    this.rowsPerPageLocalStorageKey = cloudInsightsEventsRowsPerPage;
    this.updateColumns();
  },

  methods: {
    handleColumnsChange() {
      this.updateColumns();
    },

    isComponentLoading(slotProps) {
      return slotProps.loading || this.mainLoading || this.isLoading;
    },

    shouldShowEmptyState(slotProps) {
      return slotProps.showEmptyState || !slotProps.tableData.length;
    },

    updateColumns() {
      let selectedColumnNames = defaultColumnsData.map(columnConfig => columnConfig.label);

      if (cloudInsightsEventsSelectedColumns in localStorage) {
        selectedColumnNames = JSON.parse(localStorage.getItem(cloudInsightsEventsSelectedColumns));
      }

      const selectedColumnsData = selectedColumnNames
        .map(selected => (columnsData.find((column) => (column.label === selected))));
      this.selectedColumns = selectedColumnsData.map(columnConfig => Column(columnConfig));
    },
  },
};
</script>

<style scoped>
.collectionWrapper {
  padding-left: 0;
}

.toolbar {
  padding-left: var(--jc-spacer);
}

.upgradeNudge {
  margin-bottom: var(--jc-spacer-medium);
  margin-top: var(--jc-spacer-small);
}
</style>
